import { Modal } from '@/components/modal/Modal';
import { HarborAssociationRequest } from '@/services/HarborAssociationService';
import { formatDisplayDate } from '@/utils/Date';

interface HarborAssociationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  associationData: HarborAssociationRequest | null;
}

export const HarborAssociationDetailsModal = ({
  open,
  onClose,
  associationData,
}: HarborAssociationDetailsModalProps) => {
  if (!associationData) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="!flex items-center justify-center p-4"
      aria-labelledby="association-details-title"
      aria-describedby="association-details-description"
    >
      <div
        className="bg-gray-100 p-4 sm:p-6 lg:p-8 w-full max-w-2xl mx-auto rounded-xl shadow-2xl flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <h1
          id="association-details-title"
          className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6"
        >
          Harbor Association Details
        </h1>
        <p id="association-details-description" className="sr-only">
          View detailed information about this harbor association request
          including harbor information, club information, and request status
        </p>

        <div
          className="flex-1 overflow-y-auto max-h-[70vh] pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          tabIndex={0}
          role="region"
          aria-label="Harbor association details content"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Harbor Information */}
            <section
              className="bg-gray-200 p-3 sm:p-4 rounded-lg"
              aria-labelledby="harbor-info-title"
            >
              <h2
                id="harbor-info-title"
                className="font-medium text-gray-900 mb-3 text-sm sm:text-base"
              >
                Harbor Information
              </h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 block">Name</span>
                  <span className="font-medium">
                    {associationData.harbor.name}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Location</span>
                  <span className="font-medium">
                    {associationData.harbor.location}
                  </span>
                </div>
                {associationData.harbor.description && (
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Description
                    </span>
                    <span className="font-medium break-words">
                      {associationData.harbor.description}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Club Information */}
            <section
              className="bg-gray-200 p-3 sm:p-4 rounded-lg"
              aria-labelledby="club-info-title"
            >
              <h3
                id="club-info-title"
                className="font-medium text-gray-900 mb-3 text-sm sm:text-base"
              >
                Club Information
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 block">Name</span>
                  <span className="font-medium">
                    {associationData.club.name}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">
                    Abbreviation
                  </span>
                  <span className="font-medium">
                    {associationData.club.abbreviation}
                  </span>
                </div>
              </div>
            </section>

            {/* Request Information */}
            <section
              className="bg-gray-200 p-3 sm:p-4 rounded-lg lg:col-span-2"
              aria-labelledby="request-info-title"
            >
              <h3
                id="request-info-title"
                className="font-medium text-gray-900 mb-3 text-sm sm:text-base"
              >
                Request Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500 block">Status</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium inline-block
                    ${
                      associationData.status === 'pending'
                        ? 'bg-yellow-100 dark:bg-yellow-900 contrast:bg-yellow-900 text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200'
                        : associationData.status === 'approved'
                          ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900 text-green-800 dark:text-green-200 contrast:text-green-200'
                          : associationData.status === 'rejected'
                            ? 'bg-red-100 dark:bg-red-900 contrast:bg-red-900 text-red-800 dark:text-red-200 contrast:text-red-200'
                            : associationData.status === 'revoked'
                              ? 'bg-orange-100 dark:bg-orange-900 contrast:bg-orange-900 text-orange-800 dark:text-orange-200 contrast:text-orange-200'
                              : associationData.status === 'approved_by_admin'
                                ? 'bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-200'
                                : associationData.status ===
                                    'club_approval_pending'
                                  ? 'bg-purple-100 dark:bg-purple-900 contrast:bg-purple-900 text-purple-800 dark:text-purple-200 contrast:text-purple-200'
                                  : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {(() => {
                      switch (associationData.status) {
                        case 'pending':
                          return 'Pending';
                        case 'approved':
                          return 'Approved';
                        case 'rejected':
                          return 'Rejected';
                        case 'revoked':
                          return 'Revoked';
                        case 'approved_by_admin':
                          return 'Approved by Admin';
                        case 'club_approval_pending':
                          return 'Club Approval Pending';
                        default:
                          return 'Unknown';
                      }
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">
                    Request Date
                  </span>
                  <span className="font-medium">
                    {formatDisplayDate(associationData.created_at)}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sm text-gray-500 block">
                    Request Message
                  </span>
                  <span className="font-medium break-words">
                    {associationData.request_message || '-'}
                  </span>
                </div>
                {associationData.response_message && (
                  <div className="md:col-span-2">
                    <span className="text-sm text-gray-500 block">
                      Response Message
                    </span>
                    <span className="font-medium break-words">
                      {associationData.response_message}
                    </span>
                  </div>
                )}
                {associationData.status !== 'pending' && (
                  <div>
                    <span className="text-sm text-gray-500 block">
                      Last Updated
                    </span>
                    <span className="font-medium">
                      {formatDisplayDate(associationData.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className="mt-4 sm:mt-6 lg:mt-8 flex justify-end pt-4 border-t">
          <button
            className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full sm:w-auto"
            onClick={onClose}
            aria-describedby="close-help"
          >
            Close
          </button>
          <div id="close-help" className="sr-only">
            Close this dialog and return to the previous page
          </div>
        </div>
      </div>
    </Modal>
  );
};
